import React, { useState, useContext, useEffect } from 'react';
import {
  Field,
  Placeholder,
  Image,
  ImageField,
  ComponentRendering,
  SitecoreContext,
  withSitecoreContext,
  WithSitecoreContextProps,
} from '@sitecore-jss/sitecore-jss-react';
import { AppBar, Tabs, Tab, Box, Typography, Badge } from '@mui/material';
import { styled } from '@mui/material/styles';
import { APIWebstoreContext } from '../../context/globalContext';
import './index.scss';
import { LOB_LIST } from '../../context/reducer';

export type LOBListFieldProps = {
  Title: Field<string>;
  Description: Field<string>;
  DesktopImage: ImageField;
  MobileImage: ImageField;
  ImageHeight: Field<string>;
  ImageWidth: Field<string>;
  PlaceholderName: Field<string>;
  CartEmptyText: Field<string>;
  Id: Field<string>;
};

export type LOB = {
  fields: LOBListFieldProps;
  displayName: string;
};

export type LOBListDetailsProps = WithSitecoreContextProps & {
  fields: {
    LOBs: Array<LOB>;
  };
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
  rendering: ComponentRendering;
};

const CartLOBListDetails = (props: LOBListDetailsProps): React.JSX.Element => {
  const [value, setValue] = useState(0);
  const { state } = useContext(APIWebstoreContext);

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    const lobParam = new URLSearchParams(window.location.search).get('lob')?.toUpperCase();
    const index = LOB_LIST.findIndex((lob) => lob === lobParam);
    if (index !== -1) {
      setValue(index);
    }
  }, []);

  function getCartHeaderText() {
    let lobIdentifier = props.fields?.LOBs?.[value]?.fields.Id.value;
    let lobCount = state?.cartCounts?.lobCounts?.[lobIdentifier] ?? 0;
    let LOBTitle = props.fields?.LOBs?.[value]?.displayName;
    if ((state?.cartCounts?.totalCount ?? 0) > 0) {
      return (
        <h1 className="cartPageTitle">
          {LOBTitle} Cart
          <span>
            {' '}
            ({lobCount} {lobCount > 1 ? 'Items' : 'Item'})
          </span>
        </h1>
      );
    } else {
      return (
        <div className="empty-cart">
          <h1 className="cartPageTitle red-title">Your Cart is empty</h1>
        </div>
      );
    }
  }

  const StyledBadge = styled(Badge)(() => ({
    '& .MuiBadge-badge': {
      top: -10,
      right: 0,
      position: 'absolute',
      backgroundColor: '#cd163f',
      color: 'white',
      fontSize: '1rem',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 20,
    },
  }));

  if (props.fields && props.fields.LOBs && props.fields.LOBs.length > 0) {
    return (
      <div className="cartloblistdetails">
        {props.sitecoreContext.pageEditing && <p>CartLOBListDetails Component Edit Mode</p>}

        {getCartHeaderText()}

        <AppBar position="static" color="default" className="cartLOBTabView">
          <Tabs
            value={value}
            onChange={handleTabChange}
            aria-label="Cart LOB Tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {props.fields?.LOBs?.map((LOB, index) => {
              let badgeCount = state?.cartCounts?.lobCounts?.[LOB.fields?.Id?.value] ?? 0;
              return (
                <Tab
                  key={index}
                  label={
                    <div className="tab-label" style={{ position: 'relative' }}>
                      <StyledBadge badgeContent={badgeCount} color="secondary">
                        <Image
                          field={LOB.fields?.DesktopImage}
                          className="tab-image"
                          style={{
                            width: `${LOB.fields?.ImageWidth?.value}px`,
                            height: `${LOB.fields?.ImageHeight?.value}px`,
                          }}
                        />
                      </StyledBadge>
                      <Typography>{LOB.fields?.Title?.value}</Typography>
                    </div>
                  }
                  value={index}
                  id={`tab-${LOB.fields?.Title?.value}`}
                  className="tab-lob-button"
                  aria-controls={`tabpanel-${LOB.fields?.Title?.value}`}
                />
              );
            })}
          </Tabs>
        </AppBar>

        <Box p={3} className="cartLOBProductContainer">
          <TabPanel value={value} index={0}>
            <div>
              <Placeholder
                name="standards_cart_container"
                rendering={props.rendering}
                fields={{ CartEmptyText: props.fields?.LOBs[value]?.fields?.CartEmptyText }}
              />
            </div>
          </TabPanel>

          <TabPanel value={value} index={1}>
            <div>
              <Placeholder
                name="icp_cart_container"
                rendering={props.rendering}
                fields={{ CartEmptyText: props.fields?.LOBs[value]?.fields?.CartEmptyText }}
              />
            </div>
          </TabPanel>

          <TabPanel value={value} index={2}>
            <div>
              <Placeholder
                name="training_cart_container"
                rendering={props.rendering}
                fields={{ CartEmptyText: props.fields?.LOBs[value]?.fields?.CartEmptyText }}
              />
            </div>
          </TabPanel>
        </Box>
      </div>
    );
  }

  return <></>;
};
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-force-tabpanel-${index}`}
      aria-labelledby={`scrollable-force-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

export default withSitecoreContext()(CartLOBListDetails);
